import React, { memo } from "react";
import { Box, Typography, Button, LinearProgress, useTheme, useMediaQuery } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSelector } from "react-redux";

interface StickyFooterProps {
  progress: number; // 0-100
  onContinue: () => void;
  continueLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  mobileOnly?: boolean;
  onBack?: () => void; // Optional back button handler
}

const BRAND = "#0E6B5E";

const StickyFooter: React.FC<StickyFooterProps> = ({
  progress,
  onContinue,
  continueLabel = "Continue",
  disabled = false,
  loading = false,
  mobileOnly = true,
  onBack,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isStepsCompleted = useSelector(
    (state: any) => state?.eligibilityCheck?.isStepsCompleted,
  );

  const isSuccessEligible = useSelector(
    (state: any) => state?.eligibilityCheck?.isSuccessEligible,
  );

  if (mobileOnly && !isMobile) return null;

  const pct = Math.max(0, Math.min(100, Math.round(progress)));

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        bgcolor: "#fff",
        px: 3,
        pt: 4,
        pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        boxShadow: "0 -6px 24px rgba(0,0,0,0.08)",
      }}
    >
      {isStepsCompleted && isSuccessEligible && (
        <Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.6rem", md: "2rem" },
              color: "primary.main",
              mb: 1.5,
              lineHeight: 1.2,
            }}
          >
            Ready to fly?{" "}
            <Box component="span" sx={{ display: "block" }}>
              Let's finalize.
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              lineHeight: 1.6,
              fontWeight: 500,
              mb: 3,
            }}
          >
            To move to the final verification phase, we just need to
            verify your identity using your PAN.
          </Typography>
        </Box>)}
      {/* Progress header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: BRAND,
            textTransform: "uppercase",
          }}
        >
          Eligibility Check
        </Typography>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: BRAND,
          }}
        >
          {pct}% Complete
        </Typography>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: "#EEF1F0",
          mb: 1.75,
          "& .MuiLinearProgress-bar": {
            bgcolor: BRAND,
            borderRadius: 999,
          },
        }}
      />

      {/* Continue button */}
      <Box sx={{ display: "flex", gap: 1 }}>
        {onBack && (
          <Button
            variant="outlined"
            onClick={onBack}
            sx={{ flex: 1, py: 1.75, fontSize: "1rem", fontWeight: 600 }}
          >
            Back
          </Button>)}
        <Button
          fullWidth
          onClick={onContinue}
          disabled={disabled || loading}
          endIcon={<ArrowForwardIcon />}
          sx={{
            bgcolor: BRAND,
            color: "#fff",
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 700,
            py: 1.5,
            borderRadius: "12px",
            boxShadow: "none",
            "&:hover": { bgcolor: "#0A5749", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "#9EC4BD", color: "#fff" },
            "& .MuiButton-endIcon": { ml: "auto" },
            justifyContent: "space-between",
            pl: 2.5,
            pr: 2,
          }}
        >
          <Box component="span" sx={{ flex: 1, textAlign: "center" }}>
            {loading ? "Please wait..." : continueLabel}
          </Box>
        </Button>
      </Box>
    </Box>
  );
};

export default memo(StickyFooter);
